/**
 * Prompts Page
 * პრომპტების გვერდი
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@aiia/portal-core';
import {
  usePrompts,
  useCreatePrompt,
  useUpdatePrompt,
  useUpdateRagConfig,
  useActivatePrompt,
  useDeactivatePrompt,
  useAddFileToPrompt,
  useRemoveFileFromPrompt,
  useDeletePrompt,
} from '../hooks/usePrompts';
import { useDataFiles } from '../hooks/useDataFiles';
import { getApiErrorMessage } from '../utils/errorMessages';
import {
  type Prompt,
  type CreatePromptRequest,
  type UpdateRagConfigRequest,
  AI_MODELS,
} from '../features/api/prompts';

export default function PromptsPage() {
  const { t } = useTranslation();
  const notification = useNotification();
  const { prompts, isLoading, refetch } = usePrompts();
  const { dataFiles } = useDataFiles();
  const createPrompt = useCreatePrompt();
  const updatePrompt = useUpdatePrompt();
  const updateRagConfig = useUpdateRagConfig();
  const activatePrompt = useActivatePrompt();
  const deactivatePrompt = useDeactivatePrompt();
  const addFileToPrompt = useAddFileToPrompt();
  const removeFileFromPrompt = useRemoveFileFromPrompt();
  const deletePrompt = useDeletePrompt();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [showFilesModal, setShowFilesModal] = useState<Prompt | null>(null);
  const [showRagModal, setShowRagModal] = useState<Prompt | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState<Prompt | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreatePromptRequest>({
    name: '',
    description: '',
    systemPrompt: '',
    model: 'gemini-2.5-flash',
    temperature: 0.7,
    maxTokens: 2048,
    agentMode: false,
    maxIterations: 5,
  });

  // Selected files for new prompt
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);

  // RAG config state
  const [ragConfig, setRagConfig] = useState<UpdateRagConfigRequest>({
    enabled: true,
    topK: 5,
    similarityThreshold: 0.7,
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      systemPrompt: '',
      model: 'gemini-2.5-flash',
      temperature: 0.7,
      maxTokens: 2048,
      agentMode: false,
      maxIterations: 5,
    });
    setSelectedFileIds([]);
  };

  const handleOpenEdit = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description || '',
      systemPrompt: prompt.systemPrompt,
      model: prompt.model,
      temperature: prompt.temperature,
      maxTokens: prompt.maxTokens,
      agentMode: prompt.agentMode ?? false,
      maxIterations: prompt.maxIterations ?? 5,
    });
    setSelectedFileIds(prompt.files?.map(f => f.id) || []);
    setShowCreateModal(true);
  };

  const handleOpenRag = (prompt: Prompt) => {
    setShowRagModal(prompt);
    setRagConfig({
      enabled: prompt.ragConfig?.enabled ?? true,
      topK: prompt.ragConfig?.topK ?? 5,
      similarityThreshold: prompt.ragConfig?.similarityThreshold ?? 0.7,
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.systemPrompt) {
      notification.error(t('prompts.namePromptRequired'));
      return;
    }

    try {
      if (editingPrompt) {
        await updatePrompt.mutateAsync({ id: editingPrompt.id, data: formData });

        // Handle file changes for edit
        const existingFileIds = editingPrompt.files?.map(f => f.id) || [];
        const filesToAdd = selectedFileIds.filter(id => !existingFileIds.includes(id));
        const filesToRemove = existingFileIds.filter(id => !selectedFileIds.includes(id));

        for (const fileId of filesToAdd) {
          await addFileToPrompt.mutateAsync({ promptId: editingPrompt.id, fileId });
        }
        for (const fileId of filesToRemove) {
          await removeFileFromPrompt.mutateAsync({ promptId: editingPrompt.id, fileId });
        }

        notification.success(t('prompts.promptUpdated'));
      } else {
        const newPrompt = await createPrompt.mutateAsync(formData);

        // Add selected files to the new prompt
        if (selectedFileIds.length > 0 && newPrompt?.id) {
          for (const fileId of selectedFileIds) {
            await addFileToPrompt.mutateAsync({ promptId: newPrompt.id, fileId });
          }
        }

        notification.success(t('prompts.promptCreated'));
      }
      setShowCreateModal(false);
      setEditingPrompt(null);
      resetForm();
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleSaveRagConfig = async () => {
    if (!showRagModal) return;

    try {
      await updateRagConfig.mutateAsync({ id: showRagModal.id, config: ragConfig });
      notification.success(t('prompts.ragUpdated'));
      setShowRagModal(null);
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleToggleActive = async (prompt: Prompt) => {
    try {
      if (prompt.isActive) {
        await deactivatePrompt.mutateAsync(prompt.id);
        notification.success(t('prompts.promptDeactivated'));
      } else {
        await activatePrompt.mutateAsync(prompt.id);
        notification.success(t('prompts.promptActivated'));
      }
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const confirmDelete = (prompt: Prompt) => {
    setPromptToDelete(prompt);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!promptToDelete) return;
    try {
      await deletePrompt.mutateAsync(promptToDelete.id);
      notification.success(t('prompts.promptDeleted'));
      setDeleteModalOpen(false);
      setPromptToDelete(null);
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleAddFile = async (fileId: string) => {
    if (!showFilesModal || !dataFiles) return;

    try {
      await addFileToPrompt.mutateAsync({ promptId: showFilesModal.id, fileId });
      notification.success(t('prompts.fileAdded'));
      // Find the file we just added and update modal state
      const addedFile = dataFiles.find(f => f.id === fileId);
      if (addedFile) {
        setShowFilesModal(prev => prev ? {
          ...prev,
          files: [...(prev.files || []), {
            id: addedFile.id,
            fileName: addedFile.fileName,
            fileType: addedFile.fileType,
            fileSize: addedFile.fileSize,
            status: addedFile.status,
            vectorStoreId: addedFile.vectorStoreId,
            displayOrder: (prev.files?.length || 0) + 1
          }]
        } : null);
      }
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleRemoveFile = async (fileId: string) => {
    if (!showFilesModal) return;

    try {
      await removeFileFromPrompt.mutateAsync({ promptId: showFilesModal.id, fileId });
      notification.success(t('prompts.fileRemoved'));
      // Update modal - remove the file from local state
      setShowFilesModal(prev => prev ? {
        ...prev,
        files: prev.files?.filter(f => f.id !== fileId)
      } : null);
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  // Get available files (completed processing and not already attached)
  const getAvailableFiles = () => {
    if (!showFilesModal || !dataFiles) return [];
    const attachedIds = showFilesModal.files?.map(f => f.id) || [];
    return dataFiles.filter(f => f.status === 'READY' && !attachedIds.includes(f.id));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('prompts.title')}</h1>
          <p className="text-gray-600">{t('prompts.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingPrompt(null);
            setShowCreateModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('prompts.createPrompt')}
        </button>
      </div>

      {/* Prompts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {prompts?.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            {t('prompts.emptyMessage')}
          </div>
        ) : (
          prompts?.map((prompt) => (
            <div
              key={prompt.id}
              className={`bg-white rounded-lg shadow p-5 border-l-4 ${
                prompt.isActive ? 'border-green-500' : 'border-gray-300'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{prompt.name}</h3>
                  {prompt.description && (
                    <p className="text-sm text-gray-500">{prompt.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {prompt.ragConfig?.enabled && (
                    <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                      RAG
                    </span>
                  )}
                  {prompt.agentMode && (
                    <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                      {t('prompts.agentMode')}
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    prompt.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {prompt.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <p className="text-sm text-gray-700 line-clamp-3 font-mono">
                  {prompt.systemPrompt}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                <div>
                  <span className="text-gray-400">{t('prompts.model')}:</span>
                  <div className="font-medium">{prompt.model}</div>
                </div>
                <div>
                  <span className="text-gray-400">{t('prompts.temperatureLabel')}:</span>
                  <div className="font-medium">{prompt.temperature}</div>
                </div>
                <div>
                  <span className="text-gray-400">{t('prompts.maxTokens')}:</span>
                  <div className="font-medium">{prompt.maxTokens}</div>
                </div>
              </div>

              {prompt.files && prompt.files.length > 0 && (
                <div className="mb-3">
                  <span className="text-xs text-gray-400">{t('prompts.knowledgeBaseFiles')}:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {prompt.files.map(file => (
                      <span key={file.id} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                        {file.fileName}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center pt-3 border-t">
                <div className="flex gap-3">
                  <button
                    onClick={() => handleOpenEdit(prompt)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => setShowFilesModal(prompt)}
                    className="text-sm text-purple-600 hover:text-purple-800"
                  >
                    {t('prompts.files')}
                  </button>
                  <button
                    onClick={() => handleOpenRag(prompt)}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    {t('prompts.ragConfig')}
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleToggleActive(prompt)}
                    className={`text-sm ${
                      prompt.isActive
                        ? 'text-yellow-600 hover:text-yellow-800'
                        : 'text-green-600 hover:text-green-800'
                    }`}
                  >
                    {prompt.isActive ? t('prompts.deactivate') : t('prompts.activate')}
                  </button>
                  <button
                    onClick={() => confirmDelete(prompt)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {editingPrompt ? t('prompts.editPrompt') : t('prompts.createPrompt')}
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('prompts.nameLabel')}
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('prompts.namePlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('prompts.model')}
                    </label>
                    <select
                      value={formData.model}
                      onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      {AI_MODELS.map(model => (
                        <option key={model.value} value={model.value}>{model.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('prompts.descriptionLabel')}
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t('prompts.descriptionPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('prompts.systemPrompt')}
                  </label>
                  <textarea
                    value={formData.systemPrompt}
                    onChange={(e) => setFormData(prev => ({ ...prev, systemPrompt: e.target.value }))}
                    placeholder={t('prompts.systemPromptPlaceholder')}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('prompts.temperature', { value: formData.temperature })}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={formData.temperature}
                      onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>{t('prompts.precise')}</span>
                      <span>{t('prompts.creative')}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('prompts.maxTokens')}
                    </label>
                    <input
                      type="number"
                      value={formData.maxTokens}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxTokens: parseInt(e.target.value) }))}
                      min={100}
                      max={8192}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Knowledge Base Files */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('prompts.knowledgeBaseFiles')}
                  </label>
                  {dataFiles && dataFiles.filter(f => f.status === 'READY').length > 0 ? (
                    <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <div className="space-y-2">
                        {dataFiles
                          .filter(f => f.status === 'READY')
                          .map(file => (
                            <label
                              key={file.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={selectedFileIds.includes(file.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedFileIds(prev => [...prev, file.id]);
                                  } else {
                                    setSelectedFileIds(prev => prev.filter(id => id !== file.id));
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">{file.fileName}</span>
                              <span className="text-xs text-gray-400">
                                ({(file.fileSize / 1024).toFixed(1)} KB)
                              </span>
                            </label>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      {t('prompts.noProcessedFiles')}
                    </p>
                  )}
                  {selectedFileIds.length > 0 && (
                    <p className="mt-1 text-xs text-blue-600">
                      {t('common.filesSelected', { count: selectedFileIds.length })}
                    </p>
                  )}
                </div>

                {/* Agent Mode */}
                <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-gray-700">{t('prompts.agentMode')}</span>
                      <p className="text-xs text-gray-500">{t('prompts.agentModeHint')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.agentMode ?? false}
                        onChange={(e) => setFormData(prev => ({ ...prev, agentMode: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                    </label>
                  </div>
                  {formData.agentMode && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('prompts.maxIterations')}
                      </label>
                      <input
                        type="number"
                        value={formData.maxIterations ?? 5}
                        onChange={(e) => setFormData(prev => ({ ...prev, maxIterations: parseInt(e.target.value) || 5 }))}
                        min={1}
                        max={20}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">{t('prompts.maxIterationsHint')}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingPrompt(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={createPrompt.isPending || updatePrompt.isPending || addFileToPrompt.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createPrompt.isPending || updatePrompt.isPending || addFileToPrompt.isPending ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t('prompts.deleteTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('prompts.deleteConfirm', { name: promptToDelete?.name })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setPromptToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={deletePrompt.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deletePrompt.isPending ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Files Modal */}
      {showFilesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t('prompts.knowledgeBaseFilesTitle', { name: showFilesModal.name })}
            </h2>

            {/* Attached files */}
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t('prompts.attachedFiles')}</h3>
              {showFilesModal.files && showFilesModal.files.length > 0 ? (
                <div className="space-y-2">
                  {showFilesModal.files.map(file => (
                    <div key={file.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg">
                      <div>
                        <span className="font-medium">{file.fileName}</span>
                        {file.vectorStoreId && (
                          <span className="ml-2 text-xs text-green-600">{t('prompts.indexed')}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveFile(file.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        {t('common.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">{t('prompts.noFilesAttached')}</p>
              )}
            </div>

            {/* Available files */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t('prompts.availableFiles')}</h3>
              {getAvailableFiles().length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {getAvailableFiles().map(file => (
                    <div key={file.id} className="flex justify-between items-center bg-blue-50 px-3 py-2 rounded-lg">
                      <span className="font-medium">{file.fileName}</span>
                      <button
                        onClick={() => handleAddFile(file.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {t('common.add')}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">
                  {t('prompts.noProcessedFilesAvailable')}
                </p>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowFilesModal(null)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RAG Config Modal */}
      {showRagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t('prompts.ragTitle', { name: showRagModal.name })}
            </h2>

            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={ragConfig.enabled}
                  onChange={(e) => setRagConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm font-medium">{t('prompts.enableRag')}</span>
              </label>

              {ragConfig.enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('prompts.topK')}
                    </label>
                    <input
                      type="number"
                      value={ragConfig.topK}
                      onChange={(e) => setRagConfig(prev => ({ ...prev, topK: parseInt(e.target.value) }))}
                      min={1}
                      max={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t('prompts.topKHint')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('prompts.similarityThreshold', { value: ragConfig.similarityThreshold })}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={ragConfig.similarityThreshold}
                      onChange={(e) => setRagConfig(prev => ({ ...prev, similarityThreshold: parseFloat(e.target.value) }))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>{t('prompts.moreResults')}</span>
                      <span>{t('prompts.higherRelevance')}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRagModal(null)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveRagConfig}
                disabled={updateRagConfig.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {updateRagConfig.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
